import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart } from "lucide-react";

import { api } from "@/lib/api";
import useFavorites from "@/lib/useFavorites";
import { renderEventRow } from "./PublicHome";

export default function FavoritesPage() {
  const { favorites, isFavorite, toggle: toggleFav } = useFavorites();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const idsKey = useMemo(() => Array.from(favorites).sort().join(","), [favorites]);

  useEffect(() => {
    api.get("/public/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!idsKey) {
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get("/public/events/by-ids", { params: { ids: idsKey } })
      .then(r => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [idsKey]);

  const catColor = (name) => categories.find(c => c.name === name)?.color || "#334155";

  // Backend already filters by status=approved and sorts ascending. Split
  // past vs upcoming so users see their history at the bottom.
  const now = new Date();
  const past = [];
  const upcoming = [];
  for (const e of events) {
    const ref = e.end_datetime ? new Date(e.end_datetime) : new Date(e.start_datetime);
    (ref < now ? past : upcoming).push(e);
  }

  return (
    <div className="grain-bg min-h-screen">
      <div className="relative z-10 max-w-[820px] mx-auto px-4 pb-24">
        <header className="pt-8 pb-4 flex items-baseline gap-3 border-b border-border">
          <Heart className="text-rose-500 fill-rose-500" size={22} strokeWidth={2} />
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1A3C5A]"
              data-testid="favorites-title">
            Your Favorites
          </h1>
          <Link
            to="/"
            data-testid="back-home-link"
            className="ml-auto inline-flex items-center gap-1 text-slate-600 hover:text-[#1A3C5A] text-sm font-display"
          >
            <ArrowLeft size={14} strokeWidth={2} /> Back to calendar
          </Link>
        </header>

        {loading ? (
          <div className="py-16 text-center text-slate-500 font-display">Loading…</div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center text-slate-500" data-testid="favorites-empty">
            <Heart size={40} className="mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
            <div className="font-display text-lg">No favorites yet.</div>
            <div className="text-sm mt-1">
              Tap the ♥ next to any event on the{" "}
              <Link to="/" className="underline hover:text-[#1A3C5A]">calendar</Link> to save it here.
            </div>
          </div>
        ) : (
          <>
            <div className="pt-4 pb-2 flex items-baseline justify-between border-b border-border">
              <div className="font-display text-lg font-semibold tracking-tight text-[#1E293B]" data-testid="favorites-count">
                {upcoming.length} upcoming{past.length > 0 && <span className="text-slate-400 font-normal"> · {past.length} past</span>}
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-display">
                {events.length} saved
              </div>
            </div>

            <div className="divide-y divide-border" data-testid="favorites-upcoming">
              {upcoming.map((e) => renderEventRow(e, { past: false, catColor, q: "", isFavorite, toggleFav }))}
              {upcoming.length === 0 && (
                <div className="py-8 text-center text-slate-500 font-display text-sm">
                  All your saved events have already happened.
                </div>
              )}
            </div>

            {past.length > 0 && (
              <>
                <div className="pt-6 pb-2 font-display text-xs uppercase tracking-widest text-slate-500 border-b border-border">
                  Past events
                </div>
                <div className="divide-y divide-border opacity-70" data-testid="favorites-past">
                  {past.map((e) => renderEventRow(e, { past: true, catColor, q: "", isFavorite, toggleFav }))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
